import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';

import {validate as isEmail} from 'isemail';
import {useDeleteButton} from 'hooks/useDeleteButton';
import {
  Modal,
  Title,
  Label,
  Input,
  SecondaryButton,
  PrimaryButton,
  IconButton,
  ConfirmMessage,
  ButtonGroup,
  ErrorMessage,
} from 'components';

import {User} from 'api/types/userTypes';
import {addUser, editUser, deleteUser} from './redux/thunks';
import {USER_TYPE} from './userType';
import i18n from 'i18next';

type AddUserModalProps = {
  show: boolean;
  userType: string;
  organizationId?: string;
  disabled?: boolean;
  maxReached?: boolean;
  onClose: () => void;
  selectedUser?: User;
};

function AddUserModal({
  show,
  onClose,
  disabled,
  maxReached,
  userType,
  organizationId,
  selectedUser,
}: AddUserModalProps) {
  const [user, setUser] = useState<User>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const {showDeleteMessage, onShowDeleteMessage} = useDeleteButton(show);

  const dispatch = useDispatch();

  const isEditMode = !!selectedUser;
  const {name = '', email = ''} = user || {};

  useEffect(() => {
    if (selectedUser) {
      setUser(selectedUser);
    } else {
      setUser(undefined);
    }
    setErrorMessage(undefined);
  }, [selectedUser]);

  useEffect(() => {
    if (maxReached && !isEditMode) {
      setErrorMessage(
        i18n.t('manageUserPage.addUserModel.validationErrors.maxUsersReached')
      );
    } else {
      setErrorMessage(undefined);
    }
  }, [maxReached, isEditMode]);

  const onUpdate =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setUser({
        ...(user || {name: '', email: ''}),
        role: userType,
        [field]: event.target.value,
      });
    };

  const onSubmit = () => {
    if (!user) {
      setErrorMessage(
        i18n.t('manageUserPage.addUserModel.validationErrors.addName')
      );
      return;
    }

    const {email, name} = user;

    if (!name) {
      setErrorMessage(
        i18n.t('manageUserPage.addUserModel.validationErrors.addName')
      );
      return;
    }

    if (!isEmail(email)) {
      setErrorMessage(
        i18n.t('manageUserPage.addUserModel.validationErrors.invalidEmail')
      );
      return;
    }

    if (isEditMode) {
      dispatch(
        editUser(
          {
            ...selectedUser,
            ...user,
          },
          organizationId
        )
      );
    } else {
      dispatch(addUser(user, organizationId));
    }

    setUser(undefined);
    onClose();
  };

  const onDelete = () => {
    if (selectedUser) {
      dispatch(deleteUser(selectedUser, organizationId));
      onClose();
    }
  };

  let headerText = 'Learner';
  if (userType === USER_TYPE.CONTENT_CREATOR.id) {
    headerText = 'Content Creator';
  } else if (userType === USER_TYPE.ADMIN.id) {
    headerText = 'Administrator';
  }

  let header = i18n.t('manageUserPage.addUserModel.addNew', {
    text: headerText.toLowerCase(),
  });
  let confirmText = i18n.t('manageUserPage.addUserModel.confirmText');

  if (isEditMode) {
    header = i18n.t('manageUserPage.addUserModel.addNewEditMode', {
      text: headerText,
    });
    confirmText = i18n.t('manageUserPage.addUserModel.confirmTextEditMode');
  }

  return (
    <Modal show={show} onClickOutside={onClose}>
      <div>
        <Title>{header}</Title>
        <Label
          htmlFor="name"
          label={i18n.t('manageUserPage.addUserModel.nameLabel')}
        >
          <Input
            id="name"
            value={name}
            onChange={onUpdate('name')}
            className="mb-4"
            disabled={disabled}
          />
        </Label>
        <Label htmlFor="email" label={i18n.t('common.emailAddressLabel')}>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={onUpdate('email')}
            disabled={disabled}
          />
        </Label>
        {errorMessage && (
          <ErrorMessage className="my-4">{errorMessage}</ErrorMessage>
        )}
        <div className="flex mt-6 justify-between">
          <div>
            {!disabled && isEditMode && (
              <ButtonGroup>
                <IconButton type="delete" onClick={onShowDeleteMessage} />
                {showDeleteMessage && (
                  <ConfirmMessage
                    className="h-36 w-72"
                    cancelText={i18n.t(
                      'manageUserPage.addUserModel.deleteUser.cancel'
                    )}
                    onCancel={onShowDeleteMessage}
                    confirmText={i18n.t(
                      'manageUserPage.addUserModel.deleteUser.confirm'
                    )}
                    onConfirm={onDelete}
                  >
                    {i18n.t('manageUserPage.addUserModel.deleteUser.prompt')}
                  </ConfirmMessage>
                )}
              </ButtonGroup>
            )}
          </div>
          <div>
            <SecondaryButton className="h-14 w-28" onClick={onClose}>
              {i18n.t('common.cancel')}
            </SecondaryButton>
            {!disabled && !maxReached && (
              <PrimaryButton className="h-14 w-28 ml-4" onClick={onSubmit}>
                {confirmText}
              </PrimaryButton>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default AddUserModal;
